// src/components/modals/AddBusinessModal.js
import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, User, Mail, Phone } from 'lucide-react';

const AddBusinessModal = ({
  isOpen,
  onClose,
  onSubmit,
  newBusiness,
  onChange,
  clients,
  isLoading,
  validationErrors,
  duplicateNameError,
  addBusinessError
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const selectedClient = clients.find(c => c.client_id === newBusiness.client_id);

  const handleClientSelect = (clientId) => {
    onChange({ target: { name: 'client_id', value: clientId } });
    setIsDropdownOpen(false);
  };

  const truncateText = (text, maxLength = 25) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Add New Business</h2>
          <button 
            onClick={onClose}
            disabled={isLoading}
            className="disabled:opacity-50 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
              <input
                type="text"
                name="business_name"
                value={newBusiness.business_name}
                onChange={onChange}
                required
                disabled={isLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.business_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter business name (min 2 characters)"
                autoComplete="off"
              />
              {validationErrors.business_name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.business_name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Industry Type *</label>
              <input
                type="text"
                name="industry_type"
                value={newBusiness.industry_type}
                onChange={onChange}
                disabled={isLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.industry_type ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter industry type"
                autoComplete="off"
              />
              {validationErrors.industry_type && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.industry_type}</p>
              )}
            </div>
            
            {/* Custom Dropdown for Client Selection */}
            <div className="md:col-span-2" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client *</label>
              <div className="relative">
                {/* Dropdown Trigger Button */}
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  disabled={isLoading}
                  className={`w-full px-3 py-2.5 border rounded-lg text-left flex justify-between items-center disabled:bg-gray-100 hover:bg-gray-50 transition-colors ${
                    validationErrors.client_id ? 'border-red-500' : 'border-gray-300'
                  } ${isDropdownOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <User size={18} className="text-gray-400 flex-shrink-0" />
                    <div className="overflow-hidden">
                      {selectedClient ? (
                        <>
                          <div className="font-medium truncate">{selectedClient.client_name}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {selectedClient.client_email ? `📧 ${truncateText(selectedClient.client_email, 20)}` : 
                             selectedClient.client_contact ? `📞 ${selectedClient.client_contact}` : 
                             `ID: ${selectedClient.client_id}`}
                          </div>
                        </>
                      ) : (
                        <div className="text-gray-500">Select a client</div>
                      )}
                    </div>
                  </div>
                  {isDropdownOpen ? 
                    <ChevronUp size={20} className="text-gray-400 flex-shrink-0" /> : 
                    <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                  }
                </button>
                
                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {/* Dropdown Header */}
                    <div className="sticky top-0 bg-gray-50 p-3 border-b">
                      <div className="flex justify-between items-center">
                        <div className="font-medium text-sm text-gray-700">
                          Select a Client ({clients.length} available)
                        </div>
                        <div className="text-xs text-gray-500">
                          Scroll to view more
                        </div>
                      </div>
                    </div>
                    
                    {/* Client List - Compact Design */}
                    <div className="py-1">
                      {clients.length > 0 ? (
                        clients.slice(0, 50).map((client, index) => (
                          <button
                            key={client.client_id}
                            type="button"
                            onClick={() => handleClientSelect(client.client_id)}
                            className={`w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors ${
                              newBusiness.client_id === client.client_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                            } ${index < clients.length - 1 ? 'border-b border-gray-100' : ''}`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                newBusiness.client_id === client.client_id ? 'bg-blue-500' : 'bg-gray-300'
                              }`}></div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate text-gray-800">
                                  {truncateText(client.client_name, 30)}
                                </div>
                                <div className="text-xs text-gray-500 truncate flex items-center space-x-2 mt-1">
                                  {client.client_email && (
                                    <span className="inline-flex items-center">
                                      <Mail size={10} className="mr-1 flex-shrink-0" />
                                      {truncateText(client.client_email, 20)}
                                    </span>
                                  )}
                                  {client.client_contact && (
                                    <span className="inline-flex items-center">
                                      <Phone size={10} className="mr-1 flex-shrink-0" />
                                      {client.client_contact}
                                    </span>
                                  )}
                                  {!client.client_email && !client.client_contact && (
                                    <span>ID: {client.client_id}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-gray-500 text-center">
                          No clients available
                        </div>
                      )}
                    </div>
                    
                    {/* Show more indicator */}
                    {clients.length > 50 && (
                      <div className="sticky bottom-0 bg-gray-50 border-t p-2 text-center">
                        <div className="text-xs text-gray-500">
                          Showing 50 of {clients.length} clients
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {validationErrors.client_id && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.client_id}</p>
              )}
            </div>
          </div>

          {duplicateNameError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
              <strong>Duplicate Error:</strong> {duplicateNameError}
            </div>
          )}

          {addBusinessError && !duplicateNameError && (
            <div className="p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              <strong>API Error:</strong> {addBusinessError}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !newBusiness.business_name.trim() || !newBusiness.industry_type.trim() || !newBusiness.client_id}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {isLoading ? 'Adding...' : 'Add Business'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBusinessModal;